// API layer for guided-financial-planning feature

import { jsonParseBetter } from '@shared';
import { WaterlilyClient, createQueryKeys } from '@client/api/waterlily';

// Query keys for each guided planning domain
export const guidedPlanningSessionKeys = createQueryKeys('guidedPlanningSession');
export const guidedPlanningFundingSourceKeys = createQueryKeys('guidedPlanningFundingSource');
export const guidedPlanningQuoteKeys = createQueryKeys('guidedPlanningQuote');

export const guidedPlanningApi = {
  // =====================================================
  // Session API
  // =====================================================

  /**
   * Get all sessions for a client, ordered by most recent first.
   * The client decides which session to render.
   */
  getSessions: (clientId: string) =>
    WaterlilyClient.get<GuidedPlanningSessionResponse[]>(
      `/client/${clientId}/guided-planning-session`,
    ),

  /**
   * Create a new session or update an existing one.
   * - If sessionId is provided in body, updates that session.
   * - If sessionId is omitted, creates a new session.
   */
  upsertSession: (clientId: string, data: PutGuidedPlanningSessionBody) =>
    WaterlilyClient.put<GuidedPlanningSessionResponse>(
      `/client/${clientId}/guided-planning-session`,
      data,
    ),

  // =====================================================
  // Quotes API
  // =====================================================

  /**
   * Fetch quotes for the guided planning flow.
   * The server fetches selectedPolicyCategories and preferences from the planning session.
   * Returns quotes with metadata about why no quotes may be available.
   */
  getQuotes: (clientId: string, sessionId: string) =>
    WaterlilyClient.get<GuidedPlanningQuotesResponse>(
      `/client/${clientId}/guided-planning-quotes`,
      { sessionId },
    ),

  // =====================================================
  // Funding Sources API
  // =====================================================

  upsertSelfFunding: (params: {
    clientId: string;
    fundingSourceId: string | null;
    /** Pass `null` to delete the funding source. */
    fundingSourceData: SelfFundingSource | null;
  }) =>
    WaterlilyClient.put<PutMultipleFundingSourceResponse>(
      `/client/${params.clientId}/multiple-funding-source`,
      {
        clientId: params.clientId,
        fundingSourceId: params.fundingSourceId,
        fundingSourceType: 'selfFunding',
        fundingSourceData: params.fundingSourceData,
      },
    ),

  /**
   * Persist a policy funding source (ltcPolicy) for the client.
   * Used during session finalization to save the selected quote as a policy.
   */
  persistPolicyFundingSource: (params: {
    clientId: string;
    fundingSourceId: string;
    fundingSourceData: Omit<PolicyFundingSource, 'pendingFundingSourceId'>;
  }) =>
    WaterlilyClient.put<PutMultipleFundingSourceResponse>(
      `/client/${params.clientId}/multiple-funding-source`,
      {
        clientId: params.clientId,
        fundingSourceId: params.fundingSourceId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: params.fundingSourceData,
      },
    ),

  /**
   * Remove a policy funding source from the client. The server also deletes any policy
   * application started on it, so callers check `getPolicyApplications` first.
   */
  deletePolicyFundingSource: (params: { clientId: string; fundingSourceId: string }) =>
    WaterlilyClient.put<PutMultipleFundingSourceResponse>(
      `/client/${params.clientId}/multiple-funding-source`,
      {
        clientId: params.clientId,
        fundingSourceId: params.fundingSourceId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: null,
      },
    ),

  getPolicyApplications: (clientId: string) =>
    WaterlilyClient.get<ClientPolicyApplication[]>(
      `/client/${clientId}/policy-applications-with-status-updates`,
    ),

  /**
   * Batch update existingAssetsValue for multiple self-funding sources.
   * Used to apply funding deductions when finalizing a guided planning session, and to give them
   * back when a finalized session is reopened.
   *
   * The updated client container can go straight into Redux, whose reducer needs real Dates (birth
   * dates, survey dates), so it's parsed the way the Redux API middleware parses every response.
   */
  batchUpdateSelfFundingAssets: async (params: {
    clientId: string;
    updates: Array<{ fundingSourceId: string; existingAssetsValue: number }>;
  }): Promise<ClientContainer> => {
    const container = await WaterlilyClient.put<ClientContainer>(
      `/client/${params.clientId}/batch-self-funding-existing-assets-value`,
      { clientId: params.clientId, updates: params.updates },
    );
    return jsonParseBetter<ClientContainer>(JSON.stringify(container)) ?? container;
  },
};
